import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import ImageUpload from "../../components/ImageUpload";

const EMPTY = { name: "", image: "", tagline: "" };

export default function AdminOccasions() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/occasions").then((r) => setRows(r.data));
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };
  const openEdit = (o) => {
    setEditing(o);
    setForm({ name: o.name || "", image: o.image || "", tagline: o.tagline || "" });
    setOpen(true);
  };

  const save = async () => {
    if (!form.name) {
      toast.error("Name is required");
      return;
    }
    try {
      if (editing) await api.put(`/admin/occasions/${editing.id}`, form);
      else await api.post("/admin/occasions", form);
      toast.success(editing ? "Occasion updated" : "Occasion created");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this occasion?")) return;
    await api.delete(`/admin/occasions/${id}`);
    toast.success("Occasion deleted");
    load();
  };

  return (
    <div data-testid="admin-occasions">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-serif text-4xl tracking-tight">Occasions</h1>
          <p className="text-ink-soft text-sm mt-1">{rows.length} occasions</p>
        </div>
        <button onClick={openNew} className="btn-ink flex items-center gap-2 px-5 py-3 overline" data-testid="add-occ-btn">
          <Plus size={15} /> Add Occasion
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {rows.map((o) => (
          <div key={o.id} className="border border-line" data-testid={`admin-occ-${o.name}`}>
            <div className="aspect-[4/3] bg-[#efece6] overflow-hidden">
              {o.image ? <img src={o.image} alt={o.name} className="w-full h-full object-cover" /> : null}
            </div>
            <div className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-serif text-xl">{o.name}</h3>
                  {o.tagline && <p className="text-xs text-ink-soft mt-1">{o.tagline}</p>}
                </div>
                <div className="flex items-center gap-1 shrink-0">
                  <button onClick={() => openEdit(o)} className="p-2 hover:text-gold-deep"><Pencil size={14} /></button>
                  <button onClick={() => del(o.id)} className="p-2 hover:text-red-700"><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto bg-white">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editing ? "Edit Occasion" : "New Occasion"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <L label="Name"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="occ-name" /></L>
            <L label="Tagline (optional)"><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></L>
            <ImageUpload label="Occasion Image" value={form.image} onChange={(url) => setForm({ ...form, image: url })} />
            <button onClick={save} className="btn-ink w-full py-3.5 overline" data-testid="save-occ-btn">{editing ? "Save changes" : "Create occasion"}</button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function L({ label, children }) {
  return <label className="block"><span className="overline text-ink-soft mb-1.5 block">{label}</span>{children}</label>;
}
